// src/pages/driver/DriverProfilePage.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Chip,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SecurityIcon from '@mui/icons-material/Security';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import InfoIcon from '@mui/icons-material/Info';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { updateCurrentUserProfile, changeCurrentUserPassword, getCurrentUser } from '../../api/userAPI';
import { getCompany } from '../../api/companyAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import { useAuth } from '../../contexts/AuthContext';

const getRoleColor = (role) => {
  switch (role) {
    case 'SuperAdmin':
      return 'error';
    case 'Admin':
      return 'warning';
    case 'Driver':
      return 'info';
    default:
      return 'default';
  }
};

const getRoleIcon = (role) => {
  switch (role) {
    case 'SuperAdmin':
      return <SecurityIcon />;
    case 'Admin':
      return <BadgeIcon />;
    case 'Driver':
      return <PersonIcon />;
    default:
      return <PersonIcon />;
  }
};

const DriverProfilePage = () => {
  const { user: contextUser } = useAuth();
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch current user details (more comprehensive than context)
  const { data: user, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['current-user'],
    queryFn: getCurrentUser
  });

  // Fetch company details
  const { data: company } = useQuery({
    queryKey: ['company', user?.company_id],
    queryFn: () => getCompany(user.company_id),
    enabled: !!user?.company_id
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: updateCurrentUserProfile,
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
      setEditMode(false);
      refetch();
      queryClient.invalidateQueries(['current-user']);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating profile: ${error.message}`,
        severity: 'error'
      });
    }
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: changeCurrentUserPassword,
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Password changed successfully!',
        severity: 'success'
      });
      setChangePasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error changing password: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleEditStart = () => {
    setEditData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || '',
      email: user?.email || ''
    });
    setEditMode(true);
  };

  const handleEditCancel = () => {
    setEditMode(false);
    setEditData({});
  };

  const handleEditSave = () => {
    updateProfileMutation.mutate(editData);
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbar({
        open: true,
        message: 'New passwords do not match',
        severity: 'error'
      });
      return;
    }
    changePasswordMutation.mutate(passwordData);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getUserInitials = (user) => {
    if (!user) return 'D';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName[0].toUpperCase();
    } else if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'D';
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading profile'} />;
  }

  if (!user) {
    return <ErrorAlert message="User profile not found" />;
  }

  return (
    <>
    <Container maxWidth="lg">
      <Box sx={{ py: 2 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center">
            <AccountCircleIcon sx={{ mr: 2, fontSize: 36, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" gutterBottom>
                My Profile
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage your personal information and account settings
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={4}>
        {/* Profile Summary Card */}
        <Grid item xs={12}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            mb: 2
          }}>
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <Avatar 
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      fontSize: '2.5rem',
                      border: '4px solid rgba(255,255,255,0.3)'
                    }}
                  >
                    {getUserInitials(user)}
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Chip 
                      icon={getRoleIcon(user.role)}
                      label={user.role} 
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                    {user.driver_id && (
                      <Chip 
                        label={`Driver ID: ${user.driver_id}`} 
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.1)', 
                          color: 'white'
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {user.email}
                  </Typography>
                  {user.phone && (
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                      <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {user.phone}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {company ? company.CompanyName : `Company ID: ${user.company_id}`}
                  </Typography>
                </Grid>
                <Grid item>
                  <Stack spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={handleEditStart}
                      disabled={editMode}
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                      }}
                    >
                      Edit Profile
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<VpnKeyIcon />}
                      onClick={() => setChangePasswordDialog(true)}
                      sx={{ 
                        borderColor: 'rgba(255,255,255,0.5)', 
                        color: 'white',
                        '&:hover': { 
                          borderColor: 'white', 
                          bgcolor: 'rgba(255,255,255,0.1)' 
                        }
                      }}
                    >
                      Change Password
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <InfoIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                <Typography variant="h6">Account Information</Typography>
              </Box>
              
              <List disablePadding>
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <BadgeIcon color="action" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="User ID" 
                    secondary={user.user_id}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 'medium' }}
                  />
                </ListItem>
                
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PersonIcon color="action" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Role" 
                    secondary={
                      <Chip 
                        icon={getRoleIcon(user.role)}
                        label={user.role} 
                        color={getRoleColor(user.role)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    }
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
                
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <BusinessIcon color="action" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Company" 
                    secondary={company ? company.CompanyName : `Company ID: ${user.company_id}`}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 'medium' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Personal Details */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center">
                  <PersonIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                  <Typography variant="h6">Personal Details</Typography>
                </Box>
                {editMode && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleEditSave}
                      disabled={updateProfileMutation.isPending}
                      size="small"
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleEditCancel}
                      disabled={updateProfileMutation.isPending}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </Stack>
                )}
              </Box>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email Address
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editData.email}
                      onChange={(e) => handleEditChange('email', e.target.value)}
                      disabled
                      InputProps={{
                        startAdornment: <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                      helperText="Email cannot be changed"
                    />
                  ) : (
                    <Box display="flex" alignItems="center">
                      <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body1" fontWeight="medium">
                        {user.email}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      First Name
                    </Typography>
                    {editMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.first_name}
                        onChange={(e) => handleEditChange('first_name', e.target.value)}
                        placeholder="Enter first name"
                      />
                    ) : (
                      <Typography variant="body1" fontWeight="medium">
                        {user.first_name || '-'}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Last Name
                    </Typography>
                    {editMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.last_name}
                        onChange={(e) => handleEditChange('last_name', e.target.value)}
                        placeholder="Enter last name"
                      />
                    ) : (
                      <Typography variant="body1" fontWeight="medium">
                        {user.last_name || '-'}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Phone Number
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editData.phone}
                      onChange={(e) => handleEditChange('phone', e.target.value)}
                      placeholder="Enter phone number"
                      InputProps={{
                        startAdornment: <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  ) : (
                    <Box display="flex" alignItems="center">
                      <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body1" fontWeight="medium">
                        {user.phone || 'Not provided'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>

            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>
    </Container>

    {/* Change Password Dialog */}
    <Dialog open={changePasswordDialog} onClose={() => setChangePasswordDialog(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center">
          <VpnKeyIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6">Change Password</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            label="Current Password"
            type="password"
            fullWidth
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
            variant="outlined"
          />
          <TextField
            label="New Password"
            type="password"
            fullWidth
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
            variant="outlined"
            helperText="Password should be at least 8 characters long"
          />
          <TextField
            label="Confirm New Password"
            type="password"
            fullWidth
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
            variant="outlined"
            error={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword}
            helperText={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword ? 'Passwords do not match' : ''}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={() => setChangePasswordDialog(false)} size="large">
          Cancel
        </Button>
        <Button 
          onClick={handlePasswordChange}
          variant="contained"
          disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword || changePasswordMutation.isPending}
          size="large"
        >
          Change Password
        </Button>
      </DialogActions>
    </Dialog>

    {/* Snackbar for notifications */}
    <Snackbar 
      open={snackbar.open} 
      autoHideDuration={6000} 
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
        {snackbar.message}
      </Alert>
    </Snackbar>
  </>
  );
};

export default DriverProfilePage;